import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { addTab as addShipmentTab } from "../../../../../redux/slices/shipmentSlice";
import { addTab as addInvoiceTab } from "../../../../../redux/slices/invoiceSlice";
import { getUserNotifications, markNotificationRead } from "../../../../../api/notificationsApi";

const timeAgo = (isoString) => {
  if (!isoString) return "";
  const diffMs = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// module: undefined shows every category (Shipments + Invoices + anything else, e.g. EDI
// failures); "shipping" / "invoice" narrows to that tab.
const NotificationFeed = ({ module, setUnreadCount }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    let cancelled = false;
    const fetchFeed = async () => {
      setLoading(true);
      try {
        const response = await getUserNotifications({ module, page: 0, size: 15 });
        if (!cancelled) {
          setItems(response.data?.content ?? []);
        }
      } catch (err) {
        console.error("Failed to fetch notifications:", err.response?.data || err.message);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFeed();
    return () => {
      cancelled = true;
    };
  }, [module]);

  const handleOpen = useCallback((item) => {
    if (!item.isRead) {
      markNotificationRead(item.id).catch(() => {});
      setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n)));
      if (setUnreadCount) setUnreadCount((prev) => Math.max(0, prev - 1));
    }

    const referenceNumber = item.notification?.referenceNumber;
    if (!referenceNumber) return;

    if (item.notification?.sourceModule === "invoice") {
      dispatch(addInvoiceTab({
        id: `invoice-${referenceNumber}-${Date.now()}`,
        name: `Invoice: ${referenceNumber}`,
        contentType: "invoiceView",
        invoiceNumber: referenceNumber,
        isDynamic: true,
        createdAt: Date.now(),
      }));
    } else {
      dispatch(addShipmentTab({
        id: `shipment-${referenceNumber}-${Date.now()}`,
        name: `Shipment: ${referenceNumber}`,
        contentType: "shipmentView",
        shipmentNumber: referenceNumber,
        isDynamic: true,
        createdAt: Date.now(),
      }));
    }
  }, [dispatch, setUnreadCount]);

  if (loading) {
    return <div className="p-fluid-2 text-gray-600 text-fluid-sm">Loading notifications...</div>;
  }

  if (!items.length) {
    return <div className="p-fluid-2 text-gray-400 text-fluid-sm">No notifications found.</div>;
  }

  return (
    <div className="flex flex-col gap-fluid-1.5 max-h-[24rem] overflow-y-auto px-fluid-2 py-fluid-2 scrollbar-thin">
      {items.map((item) => (
        <div
          key={item.id}
          onClick={() => handleOpen(item)}
          className={`p-fluid-1.5 rounded-lg border cursor-pointer hover:bg-gray-50 ${
            item.isRead ? "bg-white" : "bg-blue-50 border-blue-200"
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <p className="text-fluid-base font-semibold text-gray-800">
              {item.notification?.title ?? "Notification"}
            </p>
            {!item.isRead && <span className="mt-1 w-2 h-2 rounded-full bg-blue-600 shrink-0" />}
          </div>
          <p className="text-fluid-sm text-gray-600">{item.notification?.message}</p>
          <p className="text-fluid-xs text-gray-400 mt-0.5">{timeAgo(item.deliveredAt)}</p>
        </div>
      ))}
    </div>
  );
};

export default NotificationFeed;
